import { Button, Select, Table, Tag } from 'antd';
import type { TableProps } from 'antd';
import { ScrollText } from 'lucide-react';
import { DateTime } from 'luxon';
import {
  AUDIT_ACTIONS,
  AUDIT_ENTITY_TYPES,
  AUDIT_LOG_SORT_FIELDS,
  IST_ZONE,
  type AuditAction,
  type AuditEntityType,
  type AuditLogEntryDto,
  type Paginated,
} from '@pams/shared';
import { AuditChange } from '@/components/AuditChange';
import { DatePicker } from '@/components/DatePicker';
import { EmptyState } from '@/components/EmptyState';
import { FilterField } from '@/components/FilterField';
import { OfficerSearchSelect } from '@/components/OfficerSearchSelect';
import { PageHeader } from '@/components/PageHeader';
import { useServerTable } from '@/hooks/useServerTable';
import { istDateTime } from '@/lib/analyticsReport';
import { api } from '@/lib/api';
import { AUDIT_ACTION_ENTITY, AUDIT_ACTION_LABEL, AUDIT_ENTITY_TYPE_LABEL, auditActionGroups } from '@/lib/auditLabels';
import { asPickerDate, fromPickerDate } from '@/lib/pickerDate';
import { queryKeys } from '@/lib/queryKeys';
import { AUDIT_LOG_COPY as COPY } from './copy';

type AuditLogSortField = (typeof AUDIT_LOG_SORT_FIELDS)[number];

/** A literal tuple at module scope — `useServerTable` keys its parsers on the joined list. */
const FILTER_KEYS = ['entityType', 'entityId', 'actorOfficerId', 'action', 'dateFrom', 'dateTo'] as const;

const ENTITY_TYPES: ReadonlySet<string> = new Set(AUDIT_ENTITY_TYPES);
const ACTIONS: ReadonlySet<string> = new Set(AUDIT_ACTIONS);
const isEntityType = (value: string | undefined): value is AuditEntityType =>
  value !== undefined && ENTITY_TYPES.has(value);
const isAction = (value: string): value is AuditAction => ACTIONS.has(value);

/** `?action=` is comma-joined — the API's own spelling — so the URL and the request are one string. */
const actionsOf = (value: string | undefined): AuditAction[] =>
  (value ?? '').split(',').filter((action): action is AuditAction => isAction(action));

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE AUDIT LOG. [ANALYTICS §8]
 *
 *  The trail has been written since [P1b] and read by nobody but the duty-history drawer. This is
 *  its general reader: `GET /audit` — [amended AUDIT-FLAG] a SUPER_ADMIN holding `access_analytics`,
 *  the same flag Usage analytics takes — one `useServerTable` over six URL filters, so a
 *  filtered view is a link an administrator can send.
 *
 *  ⚠️ NO DATE IS WRITTEN ON MOUNT. With both pickers empty the SERVER reads the last 90 IST days
 *  (`AUDIT_DEFAULT_WINDOW_DAYS`); a date written into the URL here would bake today into every link
 *  copied from this page. The placeholders say what the empty pickers mean.
 *
 *  ⚠️ A FILTER THAT MAKES ANOTHER MEANINGLESS CLEARS IT IN THE SAME WRITE (`setFilters`). A new "What"
 *  drops the entity id and the actions of the old kind; "Only this" on a row sets the type and the id
 *  together — never two writes whose midpoint is a combination nobody asked for.
 *
 *  ⚠️ A HAND-EDITED URL IS NARROWED BEFORE IT IS SENT. An unknown action or entity type would be a 422
 *  on every render; the request carries only members of the shared vocabularies, and an entity id
 *  only beside its type (the API refuses one alone).
 *
 *  ⚠️ NO OVERLAY. No `Tooltip`, `Dropdown`, `Drawer`, `Modal`, `Sheet` or `Dialog` — the seconds sit in a
 *  native `title`, and a long diff folds behind `<details>` inside `AuditChange` — so the overlay census
 *  has nothing to account for on this route.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
const AuditLogPage = () => {
  const table = useServerTable<AuditLogEntryDto, AuditLogSortField>({
    sortFields: AUDIT_LOG_SORT_FIELDS,
    defaultSortBy: 'createdAt',
    defaultSortOrder: 'desc',
    filterKeys: FILTER_KEYS,
    queryKey: (params) => queryKeys.audit.list(params),
    queryFn: (params, signal) => {
      const { entityType, entityId, actorOfficerId, action, dateFrom, dateTo } = params.filters;
      const actions = actionsOf(action);
      const knownType = isEntityType(entityType) ? entityType : undefined;
      return api.get<Paginated<AuditLogEntryDto>>('/audit', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
          /* Only what is set: an empty date is the server's window, never "today" computed here. */
          ...(knownType ? { entityType: knownType } : {}),
          ...(knownType && entityId ? { entityId } : {}),
          ...(actorOfficerId ? { actorOfficerId } : {}),
          ...(actions.length > 0 ? { action: actions.join(',') } : {}),
          ...(dateFrom ? { dateFrom } : {}),
          ...(dateTo ? { dateTo } : {}),
        },
        signal,
      });
    },
  });

  const { filters } = table.params;
  const entityType = isEntityType(filters.entityType) ? filters.entityType : undefined;
  const actions = actionsOf(filters.action);
  const items = table.query.data?.items ?? [];
  const filtered = Object.keys(filters).length > 0;

  /*
   * ⚠️ NARROWING TO A KIND OF THING KEEPS ONLY THE ACTIONS OF THAT KIND. "Officer edited" under
   * "What: Policy" matches nothing, and a filter that silently empties the page reads as a broken log.
   */
  const narrowTo = (nextType: AuditEntityType | undefined, nextEntityId?: string): void => {
    const kept = nextType === undefined ? actions : actions.filter((one) => AUDIT_ACTION_ENTITY[one] === nextType);
    table.setFilters({
      entityType: nextType,
      entityId: nextEntityId,
      action: kept.length > 0 ? kept.join(',') : undefined,
    });
  };

  /* The name a filter chip shows comes from the rows it already filtered — no second request. */
  const onlyName =
    filters.entityId === undefined
      ? null
      : (items.find((row) => row.entityId === filters.entityId)?.entityName ??
        (entityType ? AUDIT_ENTITY_TYPE_LABEL[entityType] : COPY.gone));
  const actorFallback = filters.actorOfficerId
    ? (items.find((row) => row.actorOfficerId === filters.actorOfficerId)?.actorName ?? null)
    : null;

  const columns: TableProps<AuditLogEntryDto>['columns'] = [
    {
      title: COPY.columns.when,
      dataIndex: 'createdAt',
      /* ⚠️ `key` IS the `sortBy` value — [D4.5]'s "a sortable column's key must BE its sortBy". */
      key: 'createdAt',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      sortOrder: table.params.sortOrder === 'asc' ? 'ascend' : 'descend',
      render: (createdAt: string) => (
        <time
          dateTime={createdAt}
          title={`${DateTime.fromISO(createdAt, { zone: IST_ZONE }).toFormat('d LLL yyyy, HH:mm:ss')} IST`}
          className="whitespace-nowrap"
        >
          {istDateTime(createdAt)}
        </time>
      ),
    },
    {
      title: COPY.columns.who,
      key: 'actor',
      render: (_value, row) => (
        <div className="flex min-w-0 flex-col items-start">
          <span>{row.actorName ?? COPY.unknownOfficer}</span>
          {row.actorOrgUnitName && <span className="text-xs text-muted-foreground">{row.actorOrgUnitName}</span>}
          {filters.actorOfficerId !== row.actorOfficerId && (
            <Button
              type="link"
              size="small"
              className="h-auto p-0 text-xs"
              onClick={() => table.setFilter('actorOfficerId', row.actorOfficerId)}
            >
              {COPY.onlyThisOfficer}
            </Button>
          )}
        </div>
      ),
    },
    {
      title: COPY.columns.action,
      dataIndex: 'action',
      key: 'action',
      render: (action: AuditAction) => <span className="whitespace-nowrap">{AUDIT_ACTION_LABEL[action]}</span>,
    },
    {
      title: COPY.columns.entity,
      key: 'entity',
      render: (_value, row) => (
        <div className="flex min-w-0 flex-col items-start">
          <span className="text-xs text-muted-foreground">{AUDIT_ENTITY_TYPE_LABEL[row.entityType]}</span>
          {row.entityName === null ? <span className="text-subtle">{COPY.gone}</span> : <span>{row.entityName}</span>}
          {filters.entityId !== row.entityId && (
            <Button
              type="link"
              size="small"
              className="h-auto p-0 text-xs"
              onClick={() => narrowTo(row.entityType, row.entityId)}
            >
              {COPY.onlyThis}
            </Button>
          )}
        </div>
      ),
    },
    {
      title: COPY.columns.changed,
      key: 'context',
      render: (_value, row) => (
        <AuditChange
          context={row.context}
          contextNames={row.contextNames}
          entityType={row.entityType}
          action={row.action}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        icon={ScrollText}
        title={COPY.title}
        subtitle={COPY.subtitle(table.total)}
        filters={
          <>
            <FilterField label={COPY.filters.what} className="w-48">
              <Select<AuditEntityType>
                allowClear
                className="w-full"
                placeholder={COPY.filters.whatPlaceholder}
                value={entityType}
                onChange={(value) => narrowTo(value)}
                options={AUDIT_ENTITY_TYPES.map((type) => ({ value: type, label: AUDIT_ENTITY_TYPE_LABEL[type] }))}
                data-testid="audit-filter-what"
              />
            </FilterField>
            <FilterField label={COPY.filters.action} className="w-72">
              <Select<AuditAction[]>
                mode="multiple"
                allowClear
                className="w-full"
                placeholder={COPY.filters.actionPlaceholder}
                value={actions}
                optionFilterProp="label"
                onChange={(next) => table.setFilter('action', next.length > 0 ? next.join(',') : undefined)}
                options={auditActionGroups(AUDIT_ACTIONS, entityType)}
                data-testid="audit-filter-action"
              />
            </FilterField>
            <FilterField label={COPY.filters.who} className="w-64">
              <OfficerSearchSelect
                value={filters.actorOfficerId}
                fallbackLabel={actorFallback}
                placeholder={COPY.filters.whoPlaceholder}
                onChange={(value) => table.setFilter('actorOfficerId', value)}
              />
            </FilterField>
            <FilterField label={COPY.filters.from} className="w-40">
              <DatePicker
                className="w-full"
                format="dd LLL yyyy"
                placeholder={COPY.filters.fromPlaceholder}
                value={asPickerDate(filters.dateFrom)}
                /* ⚠️ The API refuses an inverted range with a 422; the picker offers none. */
                disabledDate={(day) => {
                  const to = asPickerDate(filters.dateTo);
                  return to !== null && day.toMillis() > to.endOf('day').toMillis();
                }}
                onChange={(value) => table.setFilter('dateFrom', fromPickerDate(value) ?? undefined)}
              />
            </FilterField>
            <FilterField label={COPY.filters.to} className="w-40">
              <DatePicker
                className="w-full"
                format="dd LLL yyyy"
                placeholder={COPY.filters.toPlaceholder}
                value={asPickerDate(filters.dateTo)}
                disabledDate={(day) => {
                  const from = asPickerDate(filters.dateFrom);
                  return from !== null && day.toMillis() < from.startOf('day').toMillis();
                }}
                onChange={(value) => table.setFilter('dateTo', fromPickerDate(value) ?? undefined)}
              />
            </FilterField>
            {onlyName !== null && (
              <Tag
                closable
                className="py-1"
                onClose={() => table.setFilters({ entityId: undefined })}
                data-testid="audit-only-tag"
              >
                {COPY.filters.only(onlyName)}
              </Tag>
            )}
            <Button
              disabled={!filtered}
              onClick={() => table.setFilters(Object.fromEntries(FILTER_KEYS.map((key) => [key, undefined] as const)))}
            >
              {COPY.filters.clear}
            </Button>
          </>
        }
      />

      <Table<AuditLogEntryDto>
        {...table.tableProps}
        columns={columns}
        size="middle"
        scroll={{ x: 'max-content' }}
        locale={{
          emptyText: (
            <EmptyState
              size="sm"
              title={table.query.isError ? COPY.error : filtered ? COPY.empty.filtered : COPY.empty.unfiltered}
            />
          ),
        }}
      />
    </div>
  );
};

export default AuditLogPage;
